/**
 * 追従コンテンツ 画像表示ロジックモジュール (APNGアニメーション再生対応)
 *
 * ・対象画像（.ist-js__100thStickyContentImage）が完全に表示されたときに
 *   .ist-js__100thStickyContentImage--active クラスを付与し、
 *   各追従コンテンツアイテムの位置に応じた active index（data-index）を更新します。
 *
 * ・下方向スクロールで初めて対象が完全表示されたタイミングで、
 *   該当のAPNG画像（.ist-100thStickyContent__imageItem--XX img）のsrcをリセットして再読み込みし、
 *   アニメーションを発火させます。
 *
 * ・すでに発火済みのactive indexは再表示時に再発火しません。
 */
export class StickyContentImageManager {
  private stickyImageElement: HTMLElement | null;
  private stickyItems: NodeListOf<HTMLElement>;
  private prevScrollY: number = window.scrollY;
  private triggeredIndices: Set<number> = new Set();

  constructor() {
    this.stickyImageElement = document.querySelector(
      '.ist-js__100thStickyContentImage'
    );
    this.stickyItems = document.querySelectorAll(
      '.ist-js__100thStickyContentItem'
    );
    if (this.stickyImageElement) {
      // 初期状態は -1 としておくことで、最初の activeIndex (0) との差分を検知できるようにする
      this.stickyImageElement.dataset.index = '-1';
    }
    this.init();
  }

  private init(): void {
    window.addEventListener('scroll', this.update.bind(this));
    window.addEventListener('resize', this.update.bind(this));
    // 初期表示時にも状態を更新
    this.update();
  }

  private update(): void {
    if (!this.stickyImageElement) return;

    const currentScrollY = window.scrollY;
    const isScrollingDown = currentScrollY > this.prevScrollY;
    const imageRect = this.stickyImageElement.getBoundingClientRect();
    const isActive = imageRect.bottom <= window.innerHeight;
    let activeIndex = -1;

    // 非 active 状態の場合、active クラス削除と triggeredIndices のリセット
    if (!isActive) {
      this.stickyImageElement.classList.remove(
        'ist-js__100thStickyContentImage--active'
      );
      this.triggeredIndices.clear();
      this.stickyImageElement.dataset.index = activeIndex.toString();
    }

    // 追従コンテンツアイテムの位置から activeIndex を更新
    this.stickyItems.forEach((item, index) => {
      const itemRect = item.getBoundingClientRect();
      if (itemRect.top <= window.innerHeight / 5) {
        activeIndex = index;
      }
    });

    // 前回の activeIndex を data-index から取得
    const prevIndex = parseInt(
      this.stickyImageElement.dataset.index || '-1',
      10
    );

    // スクロールアップ時の triggeredIndices の一部リセット処理
    if (activeIndex < prevIndex) {
      this.triggeredIndices.forEach((triggeredIndex) => {
        if (triggeredIndex > activeIndex) {
          this.triggeredIndices.delete(triggeredIndex);
        }
      });
    }

    // 現在の activeIndex を data-index に更新
    this.stickyImageElement.dataset.index = activeIndex.toString();

    // 下方向スクロールかつ完全表示状態で、未発火なら APNG アニメーションを発火
    if (
      isScrollingDown &&
      isActive &&
      !this.triggeredIndices.has(activeIndex)
    ) {
      this.triggerAPNGAnimation(activeIndex);
      this.triggeredIndices.add(activeIndex);
    }

    // 画像変更（APNGアニメーション発火）後に active クラスを付与する
    if (isActive) {
      this.stickyImageElement.classList.add(
        'ist-js__100thStickyContentImage--active'
      );
    }

    this.prevScrollY = currentScrollY;
  }

  private triggerAPNGAnimation(activeIndex: number): void {
    const imageIndex = activeIndex + 1;
    const indexStr = imageIndex < 10 ? `0${imageIndex}` : `${imageIndex}`;
    const selector = `.ist-100thStickyContent__imageItem--${indexStr} img`;
    const apngImg = document.querySelector(selector) as HTMLImageElement;
    if (apngImg) {
      const originalSrc = apngImg.src.split('?')[0];
      // 画像のリセット＆再読み込みで APNG アニメーションを発火
      apngImg.src = '';
      void apngImg.offsetWidth; // 強制 reflow
      apngImg.src = `${originalSrc}?t=${new Date().getTime()}`;
    }
  }
}
